/**
 * Declarative field maps between RM data-value objects and simplified-format
 * pipe-suffix fields, per the ITS-REST simplified formats specification
 * (docs/vendor/simplified_formats.md, "RM Mappings" section).
 *
 * A suffix of "" (empty string) denotes the *bare* flat key, i.e. the value
 * is written on the path itself with no `|suffix` (e.g. DV_TEXT, DV_COUNT,
 * date/time types). `|value` is accepted as a read alias for the bare key.
 */

import type { WebTemplateNode } from "./types.ts";
import { attributesFor, hasRmType } from "../../meta/rm_attributes.ts";
import {
  type CodedTextDisplayValue,
  type CodedTextMismatch,
  completeDvCodedText,
  type FlatRmDefaultIssue,
  throwOnCodedTextMismatch,
} from "./flat_rm_defaults.ts";

type RmObject = Record<string, unknown>;

/** Code-list entry used to complete a partial DV_CODED_TEXT. */
export interface CodedTextListEntry {
  value: string;
  label?: string;
  terminology?: string;
  localizedLabels?: Record<string, string>;
}

/**
 * Template context for {@link buildDvValue} / {@link buildRmValue}.
 * Without `node` or `codes`, coded text is not completed.
 */
export interface DvValueBuildContext {
  /** Web Template node that carries the code list. */
  node?: WebTemplateNode;
  /** Code list used when `node` is omitted. */
  codes?: CodedTextListEntry[];
  /** Terminology id for `codes` when an entry does not set one. */
  terminology?: string;
  /** Composition language used to choose the rubric. */
  language?: string;
  /**
   * Collects coded-text issues. When omitted, a mismatch error throws
   * `CodedTextMismatchError` and warnings are dropped.
   */
  issues?: FlatRmDefaultIssue[];
  /** Path attached to reported issues. */
  path?: string;
  /**
   * Severity of a field the type has no FLAT form for (it would set
   * nothing): `"error"` (default), `"warning"` or `"ignore"`. Reported only
   * when {@link issues} is given.
   */
  unknownKeys?: "error" | "warning" | "ignore";
  /**
   * `|code`, `|value`, or bare key without an exact template match:
   * `"reject"` (default), `"keep"`, or `"correct"`. See `CodedTextMismatch`.
   */
  codedTextMismatch?: CodedTextMismatch;
  /**
   * Valid `|code` with a `|value` that matches no listed term: `"keep"`
   * (default), `"keep-and-set-preferred-term"`, or `"replace-with-rubric"`
   * (not recommended; EHRbase compatibility). See `CodedTextDisplayValue`.
   */
  codedTextDisplayValue?: CodedTextDisplayValue;
  /**
   * Local codes of the node's archetype. A DV_CODED_TEXT on a DV_TEXT node
   * with terminology `local` must use one of them; unchecked when omitted.
   */
  archetypeCodes?: ReadonlySet<string>;
}

export type FieldKind = "string" | "number" | "integer" | "boolean";

export interface FieldSpec {
  /** Pipe suffix ("" = bare path key). */
  suffix: string;
  /** Dotted attribute path inside the RM value object. */
  path: string[];
  kind: FieldKind;
  /** Web template input type (DECIMAL, TEXT, CODED_TEXT, DATETIME, …). */
  inputType: string;
  required?: boolean;
  /** Fixed _type values for intermediate objects along `path`. */
  intermediates?: Record<string, string>;
  /** Default leaf values applied when building (e.g. terminology ids). */
  buildDefaults?: Record<string, unknown>;
}

export interface DvFieldMap {
  rmType: string;
  fields: FieldSpec[];
}

const MAPS: Record<string, DvFieldMap> = {};

function def(rmType: string, fields: FieldSpec[]): void {
  MAPS[rmType] = { rmType, fields };
}

// --- Text & codes -----------------------------------------------------------

def("DV_TEXT", [
  { suffix: "", path: ["value"], kind: "string", inputType: "TEXT", required: true },
  { suffix: "formatting", path: ["formatting"], kind: "string", inputType: "TEXT" },
]);

def("DV_CODED_TEXT", [
  {
    suffix: "code",
    path: ["defining_code", "code_string"],
    kind: "string",
    inputType: "CODED_TEXT",
    required: true,
    intermediates: { defining_code: "CODE_PHRASE" },
  },
  { suffix: "value", path: ["value"], kind: "string", inputType: "TEXT" },
  {
    suffix: "terminology",
    path: ["defining_code", "terminology_id", "value"],
    kind: "string",
    inputType: "TEXT",
    intermediates: {
      defining_code: "CODE_PHRASE",
      terminology_id: "TERMINOLOGY_ID",
    },
    buildDefaults: { value: "local" },
  },
  {
    suffix: "preferred_term",
    path: ["defining_code", "preferred_term"],
    kind: "string",
    inputType: "TEXT",
    intermediates: { defining_code: "CODE_PHRASE" },
  },
  { suffix: "formatting", path: ["formatting"], kind: "string", inputType: "TEXT" },
]);

def("CODE_PHRASE", [
  { suffix: "code", path: ["code_string"], kind: "string", inputType: "TEXT", required: true },
  {
    suffix: "terminology",
    path: ["terminology_id", "value"],
    kind: "string",
    inputType: "TEXT",
    intermediates: { terminology_id: "TERMINOLOGY_ID" },
  },
  { suffix: "preferred_term", path: ["preferred_term"], kind: "string", inputType: "TEXT" },
]);

def("DV_ORDINAL", [
  {
    suffix: "code",
    path: ["symbol", "defining_code", "code_string"],
    kind: "string",
    inputType: "CODED_TEXT",
    required: true,
    intermediates: { symbol: "DV_CODED_TEXT", defining_code: "CODE_PHRASE" },
  },
  {
    suffix: "value",
    path: ["symbol", "value"],
    kind: "string",
    inputType: "TEXT",
    intermediates: { symbol: "DV_CODED_TEXT" },
  },
  { suffix: "ordinal", path: ["value"], kind: "integer", inputType: "INTEGER" },
  {
    suffix: "terminology",
    path: ["symbol", "defining_code", "terminology_id", "value"],
    kind: "string",
    inputType: "TEXT",
    intermediates: {
      symbol: "DV_CODED_TEXT",
      defining_code: "CODE_PHRASE",
      terminology_id: "TERMINOLOGY_ID",
    },
    buildDefaults: { value: "local" },
  },
]);

// --- Quantities & numbers ---------------------------------------------------

const NORMAL_STATUS: FieldSpec = {
  suffix: "normal_status",
  path: ["normal_status", "code_string"],
  kind: "string",
  inputType: "TEXT",
  intermediates: { normal_status: "CODE_PHRASE" },
};
const MAGNITUDE_STATUS: FieldSpec = { suffix: "magnitude_status", path: ["magnitude_status"], kind: "string", inputType: "TEXT" };
const ACCURACY: FieldSpec[] = [
  { suffix: "accuracy", path: ["accuracy"], kind: "number", inputType: "DECIMAL" },
  { suffix: "accuracy_is_percent", path: ["accuracy_is_percent"], kind: "boolean", inputType: "BOOLEAN" },
];

def("DV_QUANTITY", [
  { suffix: "magnitude", path: ["magnitude"], kind: "number", inputType: "DECIMAL", required: true },
  { suffix: "unit", path: ["units"], kind: "string", inputType: "TEXT", required: true },
  { suffix: "precision", path: ["precision"], kind: "integer", inputType: "INTEGER" },
  { suffix: "magnitude_status", path: ["magnitude_status"], kind: "string", inputType: "TEXT" },
  { suffix: "accuracy", path: ["accuracy"], kind: "number", inputType: "DECIMAL" },
  { suffix: "accuracy_is_percent", path: ["accuracy_is_percent"], kind: "boolean", inputType: "BOOLEAN" },
  { suffix: "units_system", path: ["units_system"], kind: "string", inputType: "TEXT" },
  { suffix: "units_display_name", path: ["units_display_name"], kind: "string", inputType: "TEXT" },
  NORMAL_STATUS,
]);

def("DV_COUNT", [
  { suffix: "", path: ["magnitude"], kind: "integer", inputType: "INTEGER", required: true },
  { suffix: "magnitude_status", path: ["magnitude_status"], kind: "string", inputType: "TEXT" },
  { suffix: "accuracy", path: ["accuracy"], kind: "number", inputType: "DECIMAL" },
  { suffix: "accuracy_is_percent", path: ["accuracy_is_percent"], kind: "boolean", inputType: "BOOLEAN" },
  NORMAL_STATUS,
]);

def("DV_PROPORTION", [
  { suffix: "numerator", path: ["numerator"], kind: "number", inputType: "DECIMAL", required: true },
  { suffix: "denominator", path: ["denominator"], kind: "number", inputType: "DECIMAL", required: true },
  { suffix: "type", path: ["type"], kind: "integer", inputType: "INTEGER", required: true },
  { suffix: "precision", path: ["precision"], kind: "integer", inputType: "INTEGER" },
  MAGNITUDE_STATUS,
  ...ACCURACY,
  NORMAL_STATUS,
]);

// --- Booleans, dates, times -------------------------------------------------

def("DV_BOOLEAN", [
  { suffix: "", path: ["value"], kind: "boolean", inputType: "BOOLEAN", required: true },
]);

def("DV_DATE", [
  { suffix: "", path: ["value"], kind: "string", inputType: "DATE", required: true },
  MAGNITUDE_STATUS,
  NORMAL_STATUS,
]);

def("DV_DATE_TIME", [
  { suffix: "", path: ["value"], kind: "string", inputType: "DATETIME", required: true },
  MAGNITUDE_STATUS,
  NORMAL_STATUS,
]);

def("DV_TIME", [
  { suffix: "", path: ["value"], kind: "string", inputType: "TIME", required: true },
  MAGNITUDE_STATUS,
  NORMAL_STATUS,
]);

def("DV_DURATION", [
  { suffix: "", path: ["value"], kind: "string", inputType: "DURATION", required: true },
  ...ACCURACY,
  MAGNITUDE_STATUS,
  NORMAL_STATUS,
]);

// --- URIs, identifiers, parsable, multimedia --------------------------------

def("DV_URI", [
  { suffix: "", path: ["value"], kind: "string", inputType: "TEXT", required: true },
]);

def("DV_EHR_URI", [
  { suffix: "", path: ["value"], kind: "string", inputType: "TEXT", required: true },
]);

def("DV_IDENTIFIER", [
  { suffix: "id", path: ["id"], kind: "string", inputType: "TEXT", required: true },
  { suffix: "issuer", path: ["issuer"], kind: "string", inputType: "TEXT" },
  { suffix: "assigner", path: ["assigner"], kind: "string", inputType: "TEXT" },
  { suffix: "type", path: ["type"], kind: "string", inputType: "TEXT" },
]);

def("DV_PARSABLE", [
  { suffix: "", path: ["value"], kind: "string", inputType: "TEXT", required: true },
  { suffix: "formalism", path: ["formalism"], kind: "string", inputType: "TEXT", required: true },
]);

def("DV_MULTIMEDIA", [
  {
    suffix: "",
    path: ["uri", "value"],
    kind: "string",
    inputType: "TEXT",
    intermediates: { uri: "DV_URI" },
  },
  {
    suffix: "mediatype",
    path: ["media_type", "code_string"],
    kind: "string",
    inputType: "TEXT",
    required: true,
    intermediates: { media_type: "CODE_PHRASE" },
    buildDefaults: { terminology: "IANA_media-types" },
  },
  { suffix: "size", path: ["size"], kind: "integer", inputType: "INTEGER", required: true },
  { suffix: "data", path: ["data"], kind: "string", inputType: "TEXT" },
  { suffix: "alternatetext", path: ["alternate_text"], kind: "string", inputType: "TEXT" },
  {
    suffix: "compression_algorithm",
    path: ["compression_algorithm", "code_string"],
    kind: "string",
    inputType: "TEXT",
    intermediates: { compression_algorithm: "CODE_PHRASE" },
  },
  { suffix: "integrity_check", path: ["integrity_check"], kind: "string", inputType: "TEXT" },
  {
    suffix: "integrity_check_algorithm",
    path: ["integrity_check_algorithm", "code_string"],
    kind: "string",
    inputType: "TEXT",
    intermediates: { integrity_check_algorithm: "CODE_PHRASE" },
  },
]);

// --- Parties ----------------------------------------------------------------

const PARTY_FIELDS: FieldSpec[] = [
  { suffix: "name", path: ["name"], kind: "string", inputType: "TEXT" },
  {
    suffix: "id",
    path: ["external_ref", "id", "value"],
    kind: "string",
    inputType: "TEXT",
    intermediates: { external_ref: "PARTY_REF", id: "GENERIC_ID" },
  },
  {
    suffix: "id_scheme",
    path: ["external_ref", "id", "scheme"],
    kind: "string",
    inputType: "TEXT",
    intermediates: { external_ref: "PARTY_REF", id: "GENERIC_ID" },
  },
  {
    suffix: "id_namespace",
    path: ["external_ref", "namespace"],
    kind: "string",
    inputType: "TEXT",
    intermediates: { external_ref: "PARTY_REF" },
  },
];

def("PARTY_PROXY", PARTY_FIELDS);
def("PARTY_IDENTIFIED", PARTY_FIELDS);
def("PARTY_RELATED", PARTY_FIELDS);

// -----------------------------------------------------------------------------

/** Constraint-class aliases used inside OPTs. */
const RM_TYPE_ALIASES: Record<string, string> = {
  C_QUANTITY: "DV_QUANTITY",
  C_CODED_TEXT: "DV_CODED_TEXT",
  C_TERMINOLOGY_CODE: "CODE_PHRASE",
  C_CODE_PHRASE: "CODE_PHRASE",
  C_STRING: "DV_TEXT",
  C_ORDINAL: "DV_ORDINAL",
};

export function resolveDvType(rmType: string): string {
  return RM_TYPE_ALIASES[rmType] ?? rmType;
}

export function getFieldMap(rmType: string): DvFieldMap | undefined {
  return MAPS[resolveDvType(rmType)];
}

/** Fallback map for unknown types: single bare `value` field. */
const PRIMITIVE_VALUE = /^(String|Integer|Integer64|Real|Double|Boolean|Character|Octet|Iso8601_\w+|URI)$/;

/**
 * {@link fallbackFieldMap} for a type whose `value` is a primitive (or an
 * unknown type); no fields for a known type without one (DV_STATE,
 * DV_PARAGRAPH, DV_SCALE), so a bare key is reported instead of building
 * an invalid `value`.
 */
function fallbackFor(rmType: string): DvFieldMap {
  if (!hasRmType(rmType)) return fallbackFieldMap(rmType);
  const value = attributesFor(rmType).find((a) => a.name === "value");
  return value && PRIMITIVE_VALUE.test(value.typeName) ? fallbackFieldMap(rmType) : { rmType, fields: [] };
}

export function fallbackFieldMap(rmType: string): DvFieldMap {
  return {
    rmType,
    fields: [
      { suffix: "", path: ["value"], kind: "string", inputType: "TEXT" },
    ],
  };
}

// --- Generic getters/setters over plain objects or typed instances ----------

export function rmProp(obj: unknown, key: string): unknown {
  if (obj == null || typeof obj !== "object") return undefined;
  const record = obj as RmObject;
  if (key in record) return record[key];

  let proto = Object.getPrototypeOf(obj);
  while (proto && proto !== Object.prototype) {
    const descriptor = Object.getOwnPropertyDescriptor(proto, key);
    if (descriptor?.get) {
      try {
        return descriptor.get.call(obj);
      } catch {
        return undefined;
      }
    }
    proto = Object.getPrototypeOf(proto);
  }
  return undefined;
}

export function rmPropAtPath(obj: unknown, path: string[]): unknown {
  let current: unknown = obj;
  for (const key of path) {
    current = rmProp(current, key);
    if (current == null) return undefined;
  }
  // Unwrap primitive wrapper objects exposing `.value`
  if (
    current != null && typeof current === "object" &&
    !Array.isArray(current)
  ) {
    const inner = rmProp(current, "value");
    if (
      inner != null &&
      (typeof inner === "string" || typeof inner === "number" ||
        typeof inner === "boolean")
    ) {
      return inner;
    }
  }
  return current;
}

function coerce(
  value: unknown,
  kind: FieldKind,
): string | number | boolean | undefined {
  if (value == null) return undefined;
  switch (kind) {
    case "boolean":
      if (typeof value === "boolean") return value;
      if (value === "true") return true;
      if (value === "false") return false;
      return undefined;
    case "number":
      if (typeof value === "number") return value;
      if (typeof value === "string" && value !== "") return Number(value);
      return undefined;
    case "integer": {
      const n = typeof value === "number"
        ? value
        : (typeof value === "string" && value !== ""
          ? Number(value)
          : undefined);
      return n != null && Number.isFinite(n) ? Math.trunc(n) : undefined;
    }
    default:
      return typeof value === "boolean" || typeof value === "number"
        ? value
        : String(value);
  }
}

/**
 * Extract simplified-format fields (keyed by suffix, "" = bare) from an RM
 * data value object using the declarative maps.
 */
export function extractFields(
  rmType: string,
  rmValue: unknown,
): Record<string, string | number | boolean> {
  const map = getFieldMap(rmType) ?? fallbackFieldMap(rmType);
  const out: Record<string, string | number | boolean> = {};
  for (const field of map.fields) {
    const raw = rmPropAtPath(rmValue, field.path);
    const v = coerce(raw, field.kind);
    if (v !== undefined) out[field.suffix] = v;
  }
  return out;
}

function listedCodes(node: WebTemplateNode): CodedTextListEntry[] {
  const out: CodedTextListEntry[] = [];
  for (const input of node.inputs ?? []) {
    if (input.suffix && input.suffix !== "code") continue;
    for (const item of input.list ?? []) {
      if (!item.value) continue;
      out.push({
        value: item.value,
        label: item.label,
        terminology: input.terminology,
        localizedLabels: item.localizedLabels,
      });
    }
  }
  return out;
}

function nodeTerminology(node: WebTemplateNode): string | undefined {
  return node.inputs?.find((input) => input.suffix === "code" || !input.suffix)
    ?.terminology;
}

/** Minimal node so a bare code list can drive the same completion as a Web Template node. */
function nodeFromContext(context: DvValueBuildContext): WebTemplateNode | undefined {
  if (context.node) return context.node;
  if (!context.codes?.length) return undefined;
  const terminology = context.terminology ??
    context.codes.find((code) => code.terminology)?.terminology;
  return {
    id: "value",
    rmType: "DV_CODED_TEXT",
    min: 0,
    max: 1,
    aqlPath: context.path ?? "/",
    inputs: [{
      type: "CODED_TEXT",
      suffix: "code",
      terminology,
      list: context.codes.map((code) => ({
        value: code.value,
        label: code.label,
        localizedLabels: code.localizedLabels,
      })),
    }],
  };
}

/**
 * Bare DV_CODED_TEXT key: a listed code is `|code`, otherwise `|value`.
 * Text that equals no code or rubric exactly but one code ignoring case is
 * read as that `|code` as sent, so the mismatch mode applies to it.
 * Does nothing when the caller already set `|code` or `|value`.
 */
function interpretCodedTextBareField(
  fields: Record<string, string | number | boolean | null | undefined>,
  codes: CodedTextListEntry[],
  terminology?: string,
): Record<string, string | number | boolean | null | undefined> {
  const bare = fields[""];
  if (bare == null || fields.code != null || fields.value != null) return fields;
  const text = String(bare);
  const lower = text.toLowerCase();
  const isRubric = codes.some((code) =>
    code.label === text ||
    Object.values(code.localizedLabels ?? {}).includes(text)
  );
  const folded = codes.filter((code) => code.value.toLowerCase() === lower);
  const codeHit = codes.find((code) => code.value === text) ??
    (!isRubric && folded.length === 1
      ? { ...folded[0], value: text }
      : undefined);
  const rest: Record<string, string | number | boolean | null | undefined> = {
    ...fields,
  };
  delete rest[""];
  if (codeHit) {
    const next: Record<string, string | number | boolean | null | undefined> = {
      ...rest,
      code: codeHit.value,
    };
    if (
      next.terminology == null && terminology &&
      terminology.toLowerCase() !== "local" &&
      terminology.toLowerCase() !== "openehr"
    ) {
      next.terminology = terminology;
    }
    return next;
  }
  return { ...rest, value: text };
}

/** `|code` or `|terminology` is set: the value is a DV_CODED_TEXT. */
function hasCodedTextField(
  fields: Record<string, string | number | boolean | null | undefined>,
): boolean {
  return fields.code != null || fields.terminology != null;
}

/**
 * A DV_CODED_TEXT on a DV_TEXT node needs `|code`, `|terminology` and
 * `|value` (or the bare value); `local` only with a code of the archetype.
 * Reported as `coded-text-mismatch` (an error unless `codedTextMismatch` is
 * `"keep"` or `"correct"`); without an issue list it throws.
 */
function checkCodedTextOnText(
  fields: Record<string, string | number | boolean | null | undefined>,
  context?: DvValueBuildContext,
): void {
  const issues: FlatRmDefaultIssue[] = [];
  const path = context?.path ?? context?.node?.aqlPath ?? "";
  const severity = (context?.codedTextMismatch ?? "reject") === "reject"
    ? "error"
    : "warning";
  const missing = ["code", "terminology", "value"].filter((k) =>
    fields[k] == null || fields[k] === ""
  );
  if (missing.length) {
    issues.push({
      path,
      severity,
      kind: "coded-text-mismatch",
      message: `Coded text on a DV_TEXT node needs |code, |terminology and |value; missing ${
        missing.map((k) => `|${k}`).join(", ")
      } (a DV_TEXT node has no code list or terminology to complete it from)`,
    });
  }
  const code = fields.code == null ? undefined : String(fields.code);
  if (
    fields.terminology === "local" && code && context?.archetypeCodes &&
    !context.archetypeCodes.has(code)
  ) {
    issues.push({
      path,
      severity,
      kind: "coded-text-mismatch",
      message: `Coded text on a DV_TEXT node: local code "${code}" is not in the archetype's terminology`,
    });
  }
  if (context?.issues) context.issues.push(...issues);
  else throwOnCodedTextMismatch(issues);
}

/** The bare DV_TEXT key is the coded text's `|value`. */
function textBareAsValue(
  fields: Record<string, string | number | boolean | null | undefined>,
): Record<string, string | number | boolean | null | undefined> {
  if (fields[""] == null || fields.value != null) return fields;
  const { "": bare, ...rest } = fields;
  return { ...rest, value: bare };
}

/**
 * Build an RM data value object (plain, with `_type` markers) from
 * simplified-format fields keyed by suffix ("" = bare).
 *
 * When `context` carries a Web Template node or code list, a bare
 * DV_CODED_TEXT value is read as `|code` or `|value` and the missing half is
 * filled for local / openEHR lists. Input without an exact template match is
 * handled per `context.codedTextMismatch` (default `"reject"`); without a
 * `context.issues` array a mismatch error throws `CodedTextMismatchError`.
 * Without a node or code list the bare key is not turned into a code.
 */
export function buildDvValue(
  rmType: string,
  fields: Record<string, string | number | boolean | null | undefined>,
  context?: DvValueBuildContext,
): RmObject {
  const declared = resolveDvType(rmType);
  // DV_CODED_TEXT may stand where DV_TEXT is constrained (RM subtype): its
  // `|code` / `|terminology` select it, as the writer emits them.
  const resolved = declared === "DV_TEXT" && hasCodedTextField(fields)
    ? "DV_CODED_TEXT"
    : declared;
  const codedNode = context && declared === resolved
    ? nodeFromContext(context)
    : undefined;
  const source = codedNode && resolved === "DV_CODED_TEXT"
    ? interpretCodedTextBareField(
      fields,
      listedCodes(codedNode),
      nodeTerminology(codedNode),
    )
    : declared !== resolved
    ? textBareAsValue(fields)
    : fields;
  const map = getFieldMap(resolved) ?? fallbackFor(resolved);
  const root: RmObject = { _type: resolved };
  const consumed = new Set<string>(["_type"]);

  for (const field of map.fields) {
    let value = source[field.suffix];
    consumed.add(field.suffix);
    // `|value` is a read alias for the bare suffix
    if (value == null && field.suffix === "") {
      value = source.value;
      consumed.add("value");
    }
    // DV_IDENTIFIER: the bare key is a read alias for `|id` (as EHRbase reads it)
    if (value == null && resolved === "DV_IDENTIFIER" && field.suffix === "id") {
      value = source[""];
      consumed.add("");
    }
    if (value == null) continue;
    const coerced = coerce(value, field.kind);
    if (coerced === undefined) continue;

    let current = root;
    for (let i = 0; i < field.path.length - 1; i++) {
      const key = field.path[i];
      let next = current[key] as RmObject | undefined;
      if (!next || typeof next !== "object") {
        next = { _type: field.intermediates?.[key] ?? "OBJECT" };
        current[key] = next;
      }
      current = next;
    }
    current[field.path[field.path.length - 1]] = coerced;
  }

  // A key that sets nothing is reported, never dropped silently.
  const mode = context?.unknownKeys ?? "error";
  if (context?.issues && mode !== "ignore") {
    for (const [k, v] of Object.entries(source)) {
      if (v == null || consumed.has(k)) continue;
      context.issues.push({
        path: `${context.path ?? context.node?.aqlPath ?? ""}${k ? `|${k}` : ""}`,
        severity: mode,
        kind: "unknown-key",
        message: k
          ? `${resolved} has no FLAT field "|${k}"; ignored`
          : `${resolved} has no bare FLAT value; ignored (use its fields or _<attribute> paths)`,
      });
    }
  }

  if (declared !== resolved) {
    // Coded text where DV_TEXT is constrained: there is no code list or
    // terminology to complete against, so nothing is defaulted.
    checkCodedTextOnText(source, context);
  } else {
    // Apply build defaults (e.g. default terminology) when the owning object
    // was created but the defaulted leaf is missing.
    applyBuildDefaults(resolved, root, map);
  }
  applyPartyType(resolved, root, source._type);
  if (codedNode && root._type === "DV_CODED_TEXT") {
    const issues = context?.issues ?? [];
    completeDvCodedText(
      root,
      codedNode,
      issues,
      context?.path ?? codedNode.aqlPath,
      context?.language,
      {
        mismatch: context?.codedTextMismatch,
        displayValue: context?.codedTextDisplayValue,
      },
    );
    if (!context?.issues) throwOnCodedTextMismatch(issues);
  }
  return root;
}

const PARTY_PROXY_SUBTYPES = new Set([
  "PARTY_SELF",
  "PARTY_IDENTIFIED",
  "PARTY_RELATED",
]);

/**
 * `|_type` at a PARTY_PROXY path selects the subtype (e.g. `composer|_type:
 * "PARTY_SELF"`); PARTY_SELF has no name.
 */
function applyPartyType(rmType: string, root: RmObject, type: unknown): void {
  if (!rmType.startsWith("PARTY_") || type == null) return;
  const t = String(type);
  if (!PARTY_PROXY_SUBTYPES.has(t)) return;
  root._type = t;
  if (t === "PARTY_SELF") delete root.name;
}

function applyBuildDefaults(
  rmType: string,
  root: RmObject,
  map: DvFieldMap,
): void {
  // Spec: `|normal_status` carries only the code; the terminology is openEHR's.
  const ns = root.normal_status as RmObject | undefined;
  if (ns && ns.code_string != null && ns.terminology_id == null) {
    ns.terminology_id = { _type: "TERMINOLOGY_ID", value: "openehr_normal_statuses" };
  }
  if (rmType === "DV_CODED_TEXT") {
    const dc = root.defining_code as RmObject | undefined;
    if (dc && dc.code_string != null && dc.terminology_id == null) {
      dc.terminology_id = { _type: "TERMINOLOGY_ID", value: "local" };
    }
  } else if (rmType === "CODE_PHRASE") {
    if (root.code_string != null && root.terminology_id == null) {
      root.terminology_id = { _type: "TERMINOLOGY_ID", value: "local" };
    }
  } else if (rmType === "DV_ORDINAL") {
    const symbol = root.symbol as RmObject | undefined;
    const dc = symbol?.defining_code as RmObject | undefined;
    if (dc && dc.code_string != null && dc.terminology_id == null) {
      dc.terminology_id = { _type: "TERMINOLOGY_ID", value: "local" };
    }
  } else if (rmType === "DV_MULTIMEDIA") {
    const mt = root.media_type as RmObject | undefined;
    if (mt && mt.code_string != null && mt.terminology_id == null) {
      mt.terminology_id = { _type: "TERMINOLOGY_ID", value: "IANA_media-types" };
    }
  } else if (rmType === "PARTY_IDENTIFIED" || rmType === "PARTY_PROXY") {
    root._type = "PARTY_IDENTIFIED";
    const ref = root.external_ref as RmObject | undefined;
    if (ref && ref.type == null) ref.type = "PARTY";
  }
  void map;
}

/**
 * Web template `inputs` for a leaf of the given RM type, per the simplified
 * formats spec: single-field types get one input without `suffix`; others get
 * one input per required/primary suffix.
 */
export function inputsForRmType(
  rmType: string,
): Array<{ type: string; suffix?: string }> {
  const map = getFieldMap(rmType);
  if (!map) return [{ type: "TEXT" }];
  const primary = map.fields.filter((f) => f.required || isPrimary(map, f));
  const chosen = primary.length ? primary : map.fields.slice(0, 1);
  return chosen.map((f) =>
    f.suffix === "" ? { type: f.inputType } : { type: f.inputType, suffix: f.suffix }
  );
}

function isPrimary(map: DvFieldMap, f: FieldSpec): boolean {
  // For coded text, code/value/terminology are all primary; for parties, all.
  if (map.rmType === "DV_CODED_TEXT") {
    return f.suffix === "code" || f.suffix === "value" ||
      f.suffix === "terminology";
  }
  if (map.rmType === "CODE_PHRASE") {
    return f.suffix === "code" || f.suffix === "terminology";
  }
  if (map.rmType === "DV_ORDINAL") {
    return f.suffix === "code" || f.suffix === "value" ||
      f.suffix === "ordinal";
  }
  if (
    map.rmType === "PARTY_PROXY" || map.rmType === "PARTY_IDENTIFIED" ||
    map.rmType === "PARTY_RELATED"
  ) {
    return true;
  }
  return f.suffix === "";
}
